import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"

export default class extends Controller {
  static targets = ["input", "popup", "popupOverlay", "results", "loading", "recentSearches", "categoryDropdown", "tagDropdown", "categorySearch", "tagSearch", "categoryResults", "tagResults"]

  connect() {
    console.log("search connect")
    this.debouncedSearch = debounce(this.performSearch.bind(this), 300)
    this.debouncedCategorySearch = debounce(this.loadCategories.bind(this), 300)
    this.debouncedTagSearch = debounce(this.loadTags.bind(this), 300)
    this.isOpen = false
    this.recentSearchesKey = 'specialstl_recent_searches'
    this.maxRecentSearches = 5
    this.hasResults = false
    this.selectedCategory = null
    this.selectedTags = []

    this.popupOverlayTarget.addEventListener('click', this.handleOutsideClick.bind(this))

    document.querySelectorAll('.fake-search-input').forEach(element => {
      element.addEventListener('click', (event) => {
        event.preventDefault()
        this.openPopup()
      })
    })

    this.inputTarget.value = ''
  }

  disconnect() {
    this.closePopup()
    this.popupOverlayTarget.removeEventListener('click', this.handleOutsideClick.bind(this))
    this.inputTarget.value = ''
    
    // Ensure scrollbar is unlocked when controller disconnects
    document.body.classList.remove('overflow-hidden')
  }

  toggleCategoryDropdown() {
    // Close tag dropdown if open
    if (this.tagDropdownTarget.classList.contains('block')) {
      this.tagDropdownTarget.classList.add('hidden')
      this.tagDropdownTarget.classList.remove('block')
    }

    // Toggle category dropdown
    const isHidden = this.categoryDropdownTarget.classList.contains('hidden')
    this.categoryDropdownTarget.classList.toggle('hidden')
    this.categoryDropdownTarget.classList.toggle('block')

    if (isHidden) {
      // Position dropdown below the button
      const buttonRect = event.currentTarget.getBoundingClientRect()
      const popupRect = this.popupTarget.getBoundingClientRect()
      this.categoryDropdownTarget.style.left = `${buttonRect.left - popupRect.left}px`
      this.categoryDropdownTarget.style.top = `${buttonRect.bottom - popupRect.top + 5}px`

      this.categorySearchTarget.focus()
      // Load all categories initially
      this.loadCategories('')

      // Add event listener for outside clicks
      setTimeout(() => {
        document.addEventListener('click', this.handleOutsideDropdownClick.bind(this), { once: true })
      }, 0)
    }
  }

  toggleTagDropdown() {
    // Close category dropdown if open
    if (this.categoryDropdownTarget.classList.contains('block')) {
      this.categoryDropdownTarget.classList.add('hidden')
      this.categoryDropdownTarget.classList.remove('block')
    }

    // Toggle tag dropdown
    const isHidden = this.tagDropdownTarget.classList.contains('hidden')
    this.tagDropdownTarget.classList.toggle('hidden')
    this.tagDropdownTarget.classList.toggle('block')

    if (isHidden) {
      // Position dropdown below the button
      const buttonRect = event.currentTarget.getBoundingClientRect()
      const popupRect = this.popupTarget.getBoundingClientRect()
      this.tagDropdownTarget.style.left = `${buttonRect.left - popupRect.left}px`
      this.tagDropdownTarget.style.top = `${buttonRect.bottom - popupRect.top + 5}px`

      this.tagSearchTarget.focus()
      // Load all tags initially
      this.loadTags('')

      // Add event listener for outside clicks
      setTimeout(() => {
        document.addEventListener('click', this.handleOutsideDropdownClick.bind(this), { once: true })
      }, 0)
    }
  }

  handleOutsideClick(event) {
    console.log("handleOutsideClick")
    this.closePopup()
    this.inputTarget.value = ''
  }

  handleOutsideDropdownClick(event) {
    // Check if click is outside both dropdowns
    if (!this.categoryDropdownTarget.contains(event.target) &&
        !this.tagDropdownTarget.contains(event.target) &&
        !event.target.closest('[data-action*="toggleCategoryDropdown"]') &&
        !event.target.closest('[data-action*="toggleTagDropdown"]')) {
      this.categoryDropdownTarget.classList.add('hidden')
      this.categoryDropdownTarget.classList.remove('block')
      this.tagDropdownTarget.classList.add('hidden')
      this.tagDropdownTarget.classList.remove('block')
    } else {
      // If click is on a dropdown toggle button, let the toggle method handle it
      setTimeout(() => {
        document.addEventListener('click', this.handleOutsideDropdownClick.bind(this), { once: true })
      }, 0)
    }
  }

  updateFilterDisplay() {
    // Update category button to show selection
    const categoryButton = document.querySelector('[data-action="click->search#toggleCategoryDropdown"]')
    if (categoryButton) {
      const categoryText = categoryButton.querySelector('span:last-child')
      if (categoryText) {
        categoryText.textContent = this.selectedCategory ? this.selectedCategory.name : 'Category'
        // Add visual indicator if category is selected
        if (this.selectedCategory) {
          categoryButton.classList.add('bg-blue-600', 'text-white')
          categoryButton.classList.remove('bg-slate-800', 'text-slate-300')
        } else {
          categoryButton.classList.remove('bg-blue-600', 'text-white')
          categoryButton.classList.add('bg-slate-800', 'text-slate-300')
        }
      }
    }

    // Update tag button to show selection
    const tagButton = document.querySelector('[data-action="click->search#toggleTagDropdown"]')
    if (tagButton) {
      const tagText = tagButton.querySelector('span:last-child')
      if (tagText) {
        const tagCount = this.selectedTags.length
        tagText.textContent = tagCount > 0 ? `Tags (${tagCount})` : 'Tags'
        // Add visual indicator if tags are selected
        if (tagCount > 0) {
          tagButton.classList.add('bg-blue-600', 'text-white')
          tagButton.classList.remove('bg-slate-800', 'text-slate-300')
        } else {
          tagButton.classList.remove('bg-blue-600', 'text-white')
          tagButton.classList.add('bg-slate-800', 'text-slate-300')
        }
      }
    }

    // Show/hide clear filters button
    const clearButton = document.querySelector('[data-action="click->search#clearFilters"]')
    if (clearButton) {
      if (this.selectedCategory || this.selectedTags.length > 0) {
        clearButton.style.display = 'flex'
      } else {
        clearButton.style.display = 'none'
      }
    }
  }

  openPopup() {
    console.log("openPopup")
    if (!this.isOpen) {
      this.isOpen = true
      this.popupOverlayTarget.classList.remove('invisible', 'opacity-0')
      this.popupOverlayTarget.classList.add('visible', 'opacity-100')
      this.popupTarget.classList.remove('invisible', 'opacity-0')
      this.popupTarget.classList.add('visible', 'opacity-100')
      
      // Lock scrollbar
      document.body.classList.add('overflow-hidden')
      
      setTimeout(() => {
        this.inputTarget.focus()
        this.showRecentSearches()
      }, 100)
    }
  }

  closePopup() {
    if (this.isOpen) {
      this.isOpen = false
      this.popupOverlayTarget.classList.remove('visible', 'opacity-100')
      this.popupOverlayTarget.classList.add('invisible', 'opacity-0')
      this.popupTarget.classList.remove('visible', 'opacity-100')
      this.popupTarget.classList.add('invisible', 'opacity-0')
      this.clearResults()
      
      // Unlock scrollbar
      document.body.classList.remove('overflow-hidden')
    }
  }

  handleInput(event) {
    const query = event.target.value.trim()

    if (query.length === 0) {
      this.clearResults()
      this.showRecentSearches()
      this.hasResults = false
      return
    }

    this.hideRecentSearches()

    // Only show loading if we don't have results yet
    if (!this.hasResults) {
      this.showLoading()
    }

    this.debouncedSearch(query)
  }

  handleCategorySearch(event) {
    const query = event.target.value.trim()
    this.debouncedCategorySearch(query)
  }

  handleTagSearch(event) {
    const query = event.target.value.trim()
    this.debouncedTagSearch(query)
  }

  selectCategory(event) {
    const categoryId = event.currentTarget.dataset.categoryId
    const categoryName = event.currentTarget.dataset.categoryName

    // Store selected category
    this.selectedCategory = { id: categoryId, name: categoryName }

    // Close dropdown
    this.categoryDropdownTarget.classList.add('hidden')
    this.categoryDropdownTarget.classList.remove('block')

    // Update UI to show selected category
    this.updateFilterDisplay()

    // Perform search with current query and selected category
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      this.showLoading()
      this.performSearch(query)
    }
  }

  toggleTagSelection(event) {
    const tagId = event.currentTarget.dataset.tagId
    const tagName = event.currentTarget.dataset.tagName

    // Check if tag is already selected
    const existingIndex = this.selectedTags.findIndex(tag => tag.id === tagId)

    if (existingIndex >= 0) {
      // Remove tag if already selected
      this.selectedTags.splice(existingIndex, 1)
      // Update visual indicator
      const indicator = event.currentTarget.querySelector('.tag-selection-indicator')
      if (indicator) {
        indicator.setAttribute('icon', 'mdi-checkbox-blank-outline')
      }
    } else {
      // Add tag if not selected
      this.selectedTags.push({ id: tagId, name: tagName })
      // Update visual indicator
      const indicator = event.currentTarget.querySelector('.tag-selection-indicator')
      if (indicator) {
        indicator.setAttribute('icon', 'mdi-checkbox-marked')
      }
    }

    // Update UI to show selected tags
    this.updateFilterDisplay()

    // Perform search with current query and selected tags
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      this.showLoading()
      this.performSearch(query)
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closePopup()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      this.performFullSearch()
    }
  }

  performSearch(query) {
    // Build query parameters
    let searchParams = new URLSearchParams()
    searchParams.set('q', query)

    if (this.selectedCategory) {
      searchParams.set('category', this.selectedCategory.id)
    }

    if (this.selectedTags.length > 0) {
      searchParams.set('tags', this.selectedTags.map(tag => tag.id).join(','))
    }

    fetch(`/search/suggestions?${searchParams.toString()}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.hideLoading()
      this.displayResults(data.products, data.total_count, query)
    })
    .catch(error => {
      console.error('Search error:', error)
      this.hideLoading()
      this.clearResults()
    })
  }

  performFullSearch() {
    const query = this.inputTarget.value.trim()
    if (query) {
      this.addToRecentSearches(query)

      // Build query parameters for the full search
      let searchParams = new URLSearchParams()
      searchParams.set('q', query)

      if (this.selectedCategory) {
        searchParams.set('category', this.selectedCategory.id)
      }

      if (this.selectedTags.length > 0) {
        searchParams.set('tags', this.selectedTags.map(tag => tag.id).join(','))
      }

      window.location.href = `/search?${searchParams.toString()}`
    }
  }

  loadCategories(query) {
    fetch(`/search/categories?q=${encodeURIComponent(query)}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displayCategories(data)
    })
    .catch(error => {
      console.error('Category search error:', error)
    })
  }

  loadTags(query) {
    fetch(`/search/tags?q=${encodeURIComponent(query)}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displayTags(data)
    })
    .catch(error => {
      console.error('Tag search error:', error)
    })
  }

  displayCategories(categories) {
    if (!categories || categories.length === 0) {
      this.categoryResultsTarget.innerHTML = `
        <div class="text-center text-slate-400 py-2">
          No categories found
        </div>
      `
      return
    }

    const categoriesHtml = categories.map(category => this.renderCategoryItem(category)).join('')
    this.categoryResultsTarget.innerHTML = categoriesHtml
  }

  displayTags(tags) {
    if (!tags || tags.length === 0) {
      this.tagResultsTarget.innerHTML = `
        <div class="text-center text-slate-400 py-2">
          No tags found
        </div>
      `
      return
    }

    const tagsHtml = tags.map(tag => this.renderTagItem(tag)).join('')
    this.tagResultsTarget.innerHTML = tagsHtml
  }

  renderCategoryItem(category) {
    // Check if this category is currently selected
    const isSelected = this.selectedCategory && Number(this.selectedCategory.id) === Number(category.id);
    const bgColor = isSelected ? 'bg-blue-600' : 'hover:bg-slate-700';
    const textColor = isSelected ? 'text-white' : 'text-white';

    return `
      <div class="px-2 py-1 ${bgColor} rounded cursor-pointer flex items-center justify-between"
           data-action="click->search#selectCategory"
           data-category-id="${category.id}"
           data-category-name="${category.name}">
        <span class="text-sm ${textColor}">${category.name}</span>
        <span class="text-xs ${isSelected ? 'text-blue-200' : 'text-slate-400'}">${category.products_count || 0} products</span>
      </div>
    `
  }

  renderTagItem(tag) {
    // Check if tag is selected
    const isSelected = this.selectedTags.some(selectedTag => Number(selectedTag.id) === Number(tag.id))
    const icon = isSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'

    return `
      <div class="px-2 py-1 hover:bg-slate-700 rounded cursor-pointer flex items-center justify-between"
           data-action="click->search#toggleTagSelection"
           data-tag-id="${tag.id}"
           data-tag-name="${tag.name}">
        <span class="text-sm text-white">${tag.name}</span>
        <span class="flex items-center gap-1">
          <span class="text-xs text-slate-400 mr-2">${tag.products_count || 0} products</span>
          <iconify-icon class="tag-selection-indicator" icon="${icon}"></iconify-icon>
        </span>
      </div>
    `
  }

  displayResults(products, totalCount, query) {
    // Set hasResults based on whether we have products
    this.hasResults = products.length > 0

    if (products.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="text-center text-slate-400 my-12 flex items-center justify-center">
          No products found for "${query}"
        </div>
      `
      return
    }

    const productsHtml = products.map(product => this.renderProductItem(product)).join('')

    let seeAllHtml = ''
    if (totalCount > products.length || true) {
      // Build query parameters for the full search
      let searchParams = new URLSearchParams()
      searchParams.set('q', query)

      if (this.selectedCategory) {
        searchParams.set('category', this.selectedCategory.id)
      }

      if (this.selectedTags.length > 0) {
        searchParams.set('tags', this.selectedTags.map(tag => tag.id).join(','))
      }

      seeAllHtml = `
        <div>
          <a href="/search?${searchParams.toString()}"
             class="block mt-2 text-center text-slate-400 hover:text-slate-200 transition"
             data-action="click->search#handleSearchResultClick"
             data-search-query="${query}">
            See all ${totalCount} results
          </a>
        </div>
      `
    }

    this.resultsTarget.innerHTML = productsHtml + seeAllHtml
  }

  renderProductItem(product) {
    const imageHtml = product.image_url
      ? `<img src="${product.image_url}" alt="${product.title}" class="w-12 h-12 object-cover rounded overflow-hidden">`
      : `<div class="w-12 h-12 bg-slate-700 rounded flex items-center justify-center text-xs text-slate-400">No image</div>`

    const vipBadge = product.is_vip_only
      ? `<span class="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded">VIP</span>`
      : ''

    const categoryHtml = product.category
      ? `<span class="text-xs text-slate-400">${product.category}</span>`
      : ''

    return `
      <a href="/product/${product.slug}"
         class="flex items-center gap-1 p-2 hover:bg-slate-800 transition border-b border-slate-700 last:border-b-0"
         data-action="click->search#handleSearchResultClick">
        ${imageHtml}
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <h4 class="text-sm font-bold text-white line-clamp-2">${product.title}</h4>
            ${vipBadge}
          </div>
          ${categoryHtml}
        </div>
        <div class="text-sm font-bold text-white">$${product.price}</div>
      </a>
    `
  }

  showLoading() {
    this.loadingTarget.classList.remove('hidden')
    this.resultsTarget.innerHTML = ''
  }

  hideLoading() {
    this.loadingTarget.classList.add('hidden')
  }

  clearResults() {
    this.resultsTarget.innerHTML = ''
    this.hideLoading()
    this.hasResults = false
  }

  // Recent searches methods
  getRecentSearches() {
    try {
      const searches = localStorage.getItem(this.recentSearchesKey)
      return searches ? JSON.parse(searches) : []
    } catch (error) {
      console.error('Error getting recent searches:', error)
      return []
    }
  }

  normalizeQuery(query) {
    if (!query) return ''

    // Trim whitespace
    let normalized = query.trim()

    // Replace multiple spaces with a single space
    normalized = normalized.replace(/\s+/g, ' ')

    // Remove common special characters that don't add search value
    normalized = normalized.replace(/[\"\'\.\,\;\:\!\?\(\)\[\]\{\}\-\_\+\=\*\&\^\%\$\#\@\~\`]/g, ' ')

    // Clean up any spaces created by special character removal
    normalized = normalized.replace(/\s+/g, ' ').trim()

    return normalized
  }

  addToRecentSearches(query) {
    if (!query || query.trim() === '') return

    const normalizedQuery = this.normalizeQuery(query)
    if (normalizedQuery === '') return

    const searches = this.getRecentSearches()

    // Remove the query if it already exists (to move it to the top)
    const filteredSearches = searches.filter(search => search.toLowerCase() !== normalizedQuery.toLowerCase())

    // Add the new query to the beginning
    filteredSearches.unshift(normalizedQuery)

    // Keep only the most recent searches
    const trimmedSearches = filteredSearches.slice(0, this.maxRecentSearches)

    localStorage.setItem(this.recentSearchesKey, JSON.stringify(trimmedSearches))
  }

  showRecentSearchesIfEmpty() {
    const query = this.inputTarget.value.trim()
    if (query === '') {
      this.showRecentSearches()
    }
  }

  showRecentSearches() {
    const searches = this.getRecentSearches()

    if (searches.length === 0) {
      this.recentSearchesTarget.innerHTML = `
        <div class="my-12 text-slate-400 flex items-center justify-center">
          No recent searches
        </div>
      `
      this.recentSearchesTarget.classList.remove('hidden')
      return
    }

    const searchesHtml = searches.map(search => this.renderRecentSearchItem(search)).join('')

    const clearAllButton = `
      <div class="flex justify-end mt-2">
        <button
          class="text-xs text-slate-400 hover:text-slate-200 transition px-2 py-1"
          data-action="click->search#clearAllRecentSearches">
          Clear all
        </button>
      </div>
    `

    this.recentSearchesTarget.innerHTML = `
      <div>
        <h4 class="text-sm font-medium text-slate-400 mb-2">Recent Searches</h4>
        ${searchesHtml}
        ${clearAllButton}
      </div>
    `

    this.recentSearchesTarget.classList.remove('hidden')
  }

  renderRecentSearchItem(search) {
    return `
      <div class="flex items-center justify-between p-2 hover:bg-slate-800 transition rounded group">
        <button
          class="flex-1 text-left text-sm text-white truncate flex items-center gap-1"
          data-action="click->search#useRecentSearch"
          data-search-query="${search}">
          <iconify-icon class="text-slate-400" icon="mdi-history"></iconify-icon>
          ${search}
        </button>
        <button
          class="text-slate-400 hover:text-slate-200 opacity-0 group-hover:opacity-100 transition"
          data-action="click->search#removeRecentSearch"
          data-search-query="${search}">
          <iconify-icon icon="mdi-close"></iconify-icon>
        </button>
      </div>
    `
  }

  hideRecentSearches() {
    this.recentSearchesTarget.classList.add('hidden')
    this.recentSearchesTarget.innerHTML = ''
  }

  useRecentSearch(event) {
    const query = event.currentTarget.dataset.searchQuery
    this.inputTarget.value = query
    this.hideRecentSearches()
    this.showLoading()
    this.performSearch(query)
  }

  removeRecentSearch(event) {
    event.preventDefault()
    event.stopPropagation()

    const queryToRemove = event.currentTarget.dataset.searchQuery
    const searches = this.getRecentSearches()
    const updatedSearches = searches.filter(search => search !== queryToRemove)

    localStorage.setItem(this.recentSearchesKey, JSON.stringify(updatedSearches))

    this.showRecentSearches()
  }

  clearAllRecentSearches(event) {
    event.preventDefault()
    localStorage.removeItem(this.recentSearchesKey)
    this.showRecentSearches()
  }

  handleSearchResultClick(event) {
    // Save the current search query instead of the product name
    const currentQuery = this.inputTarget.value.trim()
    if (currentQuery) {
      this.addToRecentSearches(currentQuery)
    }
  }

  clearFilters() {
    // Clear selected category
    this.selectedCategory = null

    // Clear selected tags
    this.selectedTags = []

    // Update UI
    this.updateFilterDisplay()

    // Perform search with current query (without filters)
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      this.showLoading()
      this.performSearch(query)
    }
  }
}
